import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Mail, ArrowRight, CreditCard, Sparkles } from "lucide-react";
import { CocoonMark } from "@/components/CocoonMark";

const STRIPE_PORTAL_URL = "https://billing.stripe.com/p/login/eVqeVcdlgfP5egNgDJ3cc00";
const CONTACT_EMAIL = "contact@cocoonmind.com";

const T = {
  fr: {
    eyebrow: "Paiement confirmé",
    title: "Bienvenue dans le Cocoon",
    lead: "Ton paiement a bien été reçu — un immense merci de rejoindre l'aventure. Ta curiosité a désormais un nid.",
    activateTitle: "Activer ton accès premium",
    activateBody: "Pour débloquer ton forfait, écris-nous avec l'adresse e-mail utilisée pour le paiement. On active ton accès en priorité, généralement sous quelques heures.",
    mailCta: "Nous écrire pour activer",
    mailSubject: "Activation de mon abonnement COCOON",
    mailBody: "Bonjour,\n\nJe viens de m'abonner à COCOON. Voici l'e-mail utilisé pour le paiement : \n\nMerci d'activer mon accès premium.",
    portalCta: "Gérer mon abonnement",
    enterCta: "Entrer dans le Cocoon",
    receipt: "Un reçu t'a été envoyé par Stripe.",
  },
  en: {
    eyebrow: "Payment confirmed",
    title: "Welcome to the Cocoon",
    lead: "Your payment was received — a huge thank you for joining the adventure. Your curiosity now has a nest.",
    activateTitle: "Activate your premium access",
    activateBody: "To unlock your plan, write to us using the email address you used for payment. We'll activate your access as a priority, usually within a few hours.",
    mailCta: "Email us to activate",
    mailSubject: "Activating my COCOON subscription",
    mailBody: "Hello,\n\nI've just subscribed to COCOON. Here is the email used for payment: \n\nPlease activate my premium access.",
    portalCta: "Manage my subscription",
    enterCta: "Enter the Cocoon",
    receipt: "A receipt has been sent to you by Stripe.",
  },
  es: {
    eyebrow: "Pago confirmado",
    title: "Bienvenido al Cocoon",
    lead: "Tu pago se ha recibido correctamente — mil gracias por unirte a la aventura. Tu curiosidad ya tiene un nido.",
    activateTitle: "Activar tu acceso premium",
    activateBody: "Para desbloquear tu plan, escríbenos con la dirección de correo que usaste para el pago. Activaremos tu acceso de forma prioritaria, normalmente en unas horas.",
    mailCta: "Escríbenos para activar",
    mailSubject: "Activación de mi suscripción COCOON",
    mailBody: "Hola,\n\nAcabo de suscribirme a COCOON. Este es el correo usado para el pago: \n\nPor favor, activa mi acceso premium.",
    portalCta: "Gestionar mi suscripción",
    enterCta: "Entrar en el Cocoon",
    receipt: "Stripe te ha enviado un recibo.",
  },
  nl: {
    eyebrow: "Betaling bevestigd",
    title: "Welkom in de Cocoon",
    lead: "Je betaling is goed ontvangen — heel erg bedankt dat je meedoet aan het avontuur. Je nieuwsgierigheid heeft nu een nest.",
    activateTitle: "Je premium-toegang activeren",
    activateBody: "Om je abonnement te ontgrendelen, mail ons met het e-mailadres dat je voor de betaling hebt gebruikt. We activeren je toegang met voorrang, meestal binnen enkele uren.",
    mailCta: "Mail ons om te activeren",
    mailSubject: "Activering van mijn COCOON-abonnement",
    mailBody: "Hallo,\n\nIk heb me zojuist op COCOON geabonneerd. Dit is het e-mailadres dat voor de betaling is gebruikt: \n\nActiveer alstublieft mijn premium-toegang.",
    portalCta: "Mijn abonnement beheren",
    enterCta: "De Cocoon binnengaan",
    receipt: "Stripe heeft je een ontvangstbewijs gestuurd.",
  },
};

export default function Merci() {
  const { i18n } = useTranslation();
  const L = T[(i18n.language || "fr").slice(0, 2)] || T.fr;
  const mailto = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(L.mailSubject)}&body=${encodeURIComponent(L.mailBody)}`;

  return (
    <div className="relative min-h-screen bg-background text-foreground starfield overflow-x-clip flex flex-col">
      <div className="grain-overlay" aria-hidden />
      <div className="amber-glow" aria-hidden style={{ top: "-6%", left: "50%", transform: "translateX(-50%)", width: "900px", height: "640px" }} />

      <header className="relative z-10">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-center">
          <Link to="/" className="font-display text-2xl tracking-tight"><CocoonMark withMind /></Link>
        </div>
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center px-6 py-14">
        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          className="max-w-lg w-full text-center" data-testid="merci-page"
        >
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.15, type: "spring", stiffness: 200, damping: 14 }}
            className="relative w-20 h-20 rounded-full bg-primary/12 ring-1 ring-primary/40 flex items-center justify-center mx-auto mb-8"
            style={{ boxShadow: "0 0 60px -8px hsl(28 96% 56% / 0.55)" }}
          >
            <CheckCircle2 className="text-primary" size={40} />
            <Sparkles className="absolute -top-2 -right-2 text-primary/80" size={18} />
          </motion.div>

          <p className="text-primary/80 uppercase tracking-[0.28em] text-xs mb-4" data-testid="merci-eyebrow">{L.eyebrow}</p>
          <h1 className="font-display text-gradient-orange text-4xl sm:text-5xl leading-tight pb-[0.08em] mb-5" data-testid="merci-title">{L.title}</h1>
          <p className="text-foreground/65 leading-relaxed text-base sm:text-lg mb-9">{L.lead}</p>

          {/* Activation */}
          <div className="glass rounded-2xl border border-primary/25 p-6 text-left mb-8 frame-pulse" data-testid="merci-activate-card">
            <p className="flex items-center gap-2 font-display text-xl text-gradient-orange mb-2">
              <Mail size={18} /> {L.activateTitle}
            </p>
            <p className="text-foreground/60 text-sm leading-relaxed mb-5">{L.activateBody}</p>
            <a
              href={mailto}
              data-testid="merci-activate-mail"
              className="btn-frost-orange inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm w-full sm:w-auto"
            >
              <Mail size={16} /> {L.mailCta}
            </a>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={STRIPE_PORTAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="merci-portal-btn"
              className="btn-glass-soft inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm text-foreground/80 hover:text-primary w-full sm:w-auto"
            >
              <CreditCard size={15} /> {L.portalCta}
            </a>
            <Link
              to="/entrer"
              data-testid="merci-enter-btn"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm text-foreground/70 hover:text-primary transition-colors w-full sm:w-auto"
            >
              {L.enterCta} <ArrowRight size={15} />
            </Link>
          </div>

          <p className="text-foreground/35 text-xs mt-8" data-testid="merci-receipt">{L.receipt}</p>
        </motion.div>
      </main>
    </div>
  );
}
